import React, { useEffect } from 'react';
import styled from 'styled-components';
import { PaymentStatus } from '../../../../XV2';
import { useCurrency } from '../../../../hooks/currency';
import moment from 'moment';
import { useApiQuery } from '@orderx/lab/xr/http';
import { apiMeDownloadReceiptPost } from '../../../../api/me.download-receipt.[post]';
import { DownloadButton } from '../../../download-button/DownloadButton';
import { XSButton } from '../../../../xs/component/button/XSButton';
import { XSBox } from '../../../../xs/component/box/XSBox';
import { XSSection } from '../../../../xs/component/section/XSSection';
import { XSText } from '../../../../xs/component/text/XSText';
import { XSLoadingSpinner } from '../../../../xs/component/loading/spinner/XSLoadingSpinner';
import { useXV1 } from '../../../../useXV1';
import { XV1 } from '../../../../XV1';

const Wrapper = styled.div`
    .center {
        text-align: center;
    }

    .receipt-container {
        background-color: white;
        color: black;
        position: relative;

        &:after {
            background-image: linear-gradient(-45deg, #ffffff 12px, transparent 0),
                linear-gradient(45deg, #ffffff 12px, transparent 0);
            background-repeat: repeat-x;
            background-size: 24px 24px;
            content: ' ';
            display: block;
            position: absolute;
            bottom: -24px;
            left: 0;
            width: 100%;
            height: 24px;
            transform: rotate(180deg);
        }
    }

    .divider {
        border-top: 2px dashed black;
    }

    .buttons-container {
        background-color: var(--rx-color-root);
        border: 1px solid var(--rx-color-border);
        border-radius: 40px;
        position: fixed;
        top: 4px;
        right: 4px;
        z-index: 2;
    }

    .cancelled {
        text-decoration: line-through;
    }

    .cancelled-stamp {
        font-size: 2rem;
        font-weight: bold;
        background-color: var(--rx-color-danger);
        opacity: 0.75;
        color: white;
        border-radius: var(--rx-border-radius);
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%) rotate(-15deg);
        width: 100%;
        text-align: center;
    }
`;

interface Props {
    paymentId: number;
    onClose: any;
}

export function PaymentReceiptOverlay({ paymentId, onClose }: Props) {
    const $currency = useCurrency();

    const findReceipt = useXV1(XV1.API.MeGetReceiptPost);

    useEffect(() => {
        findReceipt.run({ paymentId });
    }, []);

    const downloadPdf = useApiQuery(async () => {
        const { url } = await apiMeDownloadReceiptPost({
            body: { paymentId },
        });

        return url;
    });

    if (!findReceipt.result) {
        return <XSLoadingSpinner />;
    }

    const { locationName, paymentRef, paymentStatus, paymentDate, paymentCurrency, isDownloadable, merchant, orderX } =
        findReceipt.result;

    const divider = (
        <XSSection>
            <div className="divider" />
        </XSSection>
    );

    function renderVats(vats) {
        return (
            <XSSection>
                {vats.map(({ percent, amount }) => (
                    <XSBox key={percent} justify={'space-between'}>
                        <span>VAT {percent}%</span>
                        <span>{$currency.formatDecimal(amount, paymentCurrency)}</span>
                    </XSBox>
                ))}
            </XSSection>
        );
    }

    return (
        <Wrapper>
            <XSBox className={'buttons-container'} padding={'sm'}>
                {isDownloadable && <DownloadButton query={downloadPdf} />}
                <XSButton onClick={onClose} icon={'close'} />
            </XSBox>

            <XSBox type={'grid'} className={'receipt-container'}>
                {paymentStatus === PaymentStatus.CREATED && (
                    <div className="cancelled-stamp">Payment has not been processed</div>
                )}

                {paymentStatus === PaymentStatus.CANCELLED_OR_REFUNDED && (
                    <div className="cancelled-stamp">Payment has been cancelled</div>
                )}

                <XSSection type={'grid'} className={'center'}>
                    <XSText type={'title'}>{locationName}</XSText>
                    <span>Sales receipt</span>
                    <span>ID: {paymentRef}</span>
                </XSSection>

                <XSSection className={'center'}>
                    {merchant.companyInfoRows.map((row) => (
                        <div key={row}>{row}</div>
                    ))}
                </XSSection>

                {divider}

                <XSSection justify={'space-between'}>
                    <span>Date:</span>
                    <span>{moment(paymentDate).format('D MMMM YYYY, HH:mm')}</span>
                </XSSection>

                {divider}

                <XSSection type={'grid'}>
                    {merchant.products.map((item) => (
                        <XSBox key={item.id} type={'grid'}>
                            <XSBox justify={'space-between'}>
                                <span>{item.name}</span>
                                <span>{$currency.formatDecimal(item.price, paymentCurrency)}</span>
                            </XSBox>

                            {item.rows.map((row) => (
                                <XSBox key={row.id} justify={'space-between'}>
                                    <span>• {row.name}</span>
                                    <span>{$currency.formatDecimal(row.price, paymentCurrency)}</span>
                                </XSBox>
                            ))}
                        </XSBox>
                    ))}

                    {merchant.productsCancelled.map((item) => (
                        <XSBox key={item.id} justify={'space-between'} className={'cancelled'}>
                            <span>{item.name}</span>
                            <span>{$currency.formatDecimal(item.price, paymentCurrency)}</span>
                        </XSBox>
                    ))}
                </XSSection>

                {divider}

                {merchant.vats.length > 0 && (
                    <>
                        {renderVats(merchant.vats)}

                        {divider}
                    </>
                )}

                <XSSection justify={'space-between'}>
                    <XSText transform={'uppercase'} weight={'bold'}>
                        Total
                    </XSText>

                    <XSText weight={'bold'}>{$currency.formatDecimal(merchant.total, paymentCurrency)}</XSText>
                </XSSection>

                {orderX && (
                    <>
                        <XSSection>&nbsp;</XSSection>

                        <XSSection className={'center'}>
                            {orderX.companyInfoRows.map((row) => (
                                <div key={row}>{row}</div>
                            ))}
                        </XSSection>

                        {renderVats(orderX.vats)}

                        {divider}

                        <XSSection justify={'space-between'}>
                            <XSText transform={'uppercase'} weight={'bold'}>
                                Total service fee
                            </XSText>

                            <XSText weight={'bold'}>{$currency.formatDecimal(orderX.total, paymentCurrency)}</XSText>
                        </XSSection>
                    </>
                )}
            </XSBox>
        </Wrapper>
    );
}
